import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AUTH_DEV_BYPASS } from '../config/featureFlags';
import { fetchUiPreferences } from '../services/settingsAPI';
import { applyUiPreferences } from '../services/uiPreferencesBridge';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const resolveApiBase = () => {
  const raw = typeof process.env.REACT_APP_API_URL === 'string' ? process.env.REACT_APP_API_URL.trim() : '';
  if (raw !== '') return raw.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:5002/api';
  }
  return '/api';
};
axios.defaults.baseURL = resolveApiBase();
axios.defaults.withCredentials = true;

const DEV_USER = {
  id: 'dev-admin',
  name: 'Dev Admin',
  email: 'dev@local',
  role: 'admin',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(AUTH_DEV_BYPASS ? DEV_USER : null);
  const [token, setToken] = useState(AUTH_DEV_BYPASS ? 'auto-auth-token' : '');
  const [loading, setLoading] = useState(!AUTH_DEV_BYPASS);
  const [sessionChecked, setSessionChecked] = useState(AUTH_DEV_BYPASS);

  const syncUiPreferences = useCallback(async () => {
    if (AUTH_DEV_BYPASS) return;
    try {
      const prefs = await fetchUiPreferences();
      applyUiPreferences(prefs);
    } catch {
      /* defaults in Language/Theme providers */
    }
  }, []);

  useEffect(() => {
    axios.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : '';
  }, [token]);

  useEffect(() => {
    if (AUTH_DEV_BYPASS) {
      setLoading(false);
      setSessionChecked(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/auth/verify');
        if (cancelled) return;
        if (data?.user) {
          setUser(data.user);
          if (data.token) setToken(data.token);
          await syncUiPreferences();
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setToken('');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSessionChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [syncUiPreferences]);

  const login = useCallback(
    async ({ email, password }) => {
      if (AUTH_DEV_BYPASS) {
        return { success: true };
      }
      const { data } = await axios.post('/auth/login', { email, password });
      setToken(data.token || '');
      setUser(data.user);
      await syncUiPreferences();
      return { success: true };
    },
    [syncUiPreferences]
  );

  const logout = useCallback(async () => {
    if (AUTH_DEV_BYPASS) {
      return;
    }
    try {
      await axios.post('/auth/logout');
    } catch {
      /* cookie cleared client-side attempt still resets UI */
    }
    setToken('');
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const updateUser = () => {};

  const isAuthenticated = AUTH_DEV_BYPASS || (Boolean(user) && sessionChecked);
  const isAdmin = AUTH_DEV_BYPASS || user?.role === 'admin';
  const isStaff =
    AUTH_DEV_BYPASS ||
    ['admin', 'staff', 'receptionist'].includes(String(user?.role || ''));

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    token,
    isAuthenticated,
    isAdmin,
    isStaff,
    AUTH_DEV_BYPASS,
    sessionChecked,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
